import { useState, useEffect, useCallback } from 'react';
import { Auth } from 'aws-amplify';

/**
 * Default layout configuration for home page widgets
 */
const DEFAULT_LAYOUT = {
  version: 3,
  rows: [
    { id: 'row-1', widgets: ['active-devices-metric'] },
    { id: 'row-2', widgets: ['serial-search', 'favourite-serials'] },
    { id: 'row-3', widgets: ['power-bi-dashboard'] },
    { id: 'row-4', widgets: ['favourite-credentials'] },
  ],
  hiddenWidgets: [],
  lastModified: null,
};

/**
 * Get localStorage key for user's home layout
 * @param {string} userId - User ID
 * @returns {string} localStorage key
 */
const getStorageKey = (userId) => `homeLayout:${userId}`;

/**
 * Hook for managing home page layout state with localStorage persistence
 * @returns {Object} Layout state and management functions
 */
const useHomeLayout = () => {
  const [layout, setLayoutState] = useState(DEFAULT_LAYOUT);
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user ID on mount
  useEffect(() => {
    const loadUserId = async () => {
      try {
        const authUser = await Auth.currentAuthenticatedUser();
        const id = authUser?.attributes?.sub || authUser?.username || 'anonymous';
        setUserId(id);
      } catch (error) {
        console.warn('Error loading user for home layout:', error);
        setUserId('anonymous');
      }
    };
    loadUserId();
  }, []);

  // Load layout from localStorage when userId is available
  useEffect(() => {
    if (!userId) return;

    try {
      const key = getStorageKey(userId);
      const stored = localStorage.getItem(key);
      
      if (stored) {
        const parsed = JSON.parse(stored);
        // Handle version migrations
        if (parsed.version === 3) {
          setLayoutState(parsed);
        } else if (parsed.version === 2 || parsed.version === 1) {
          // Migration v1/v2 -> v3: Put serial-search with favourite-serials in same row
          const migratedLayout = {
            ...parsed,
            version: 3,
            rows: parsed.rows.map((row) => {
              // Remove serial-search from any row it's currently in
              const widgetsWithoutSerialSearch = row.widgets.filter(w => w !== 'serial-search');
              // Add serial-search before favourite-serials if this row has it
              if (widgetsWithoutSerialSearch.includes('favourite-serials')) {
                return {
                  ...row,
                  widgets: ['serial-search', ...widgetsWithoutSerialSearch],
                };
              }
              return { ...row, widgets: widgetsWithoutSerialSearch };
            }).filter(row => row.widgets.length > 0), // Remove empty rows
          };
          // Ensure serial-search exists somewhere (add to favourite-serials row if missing)
          const hasSerialSearch = migratedLayout.rows.some(r => r.widgets.includes('serial-search'));
          if (!hasSerialSearch && !migratedLayout.hiddenWidgets?.includes('serial-search')) {
            const serialsRowIndex = migratedLayout.rows.findIndex(r => r.widgets.includes('favourite-serials'));
            if (serialsRowIndex >= 0) {
              migratedLayout.rows[serialsRowIndex].widgets.unshift('serial-search');
            }
          }
          // Save migrated layout
          localStorage.setItem(key, JSON.stringify(migratedLayout));
          setLayoutState(migratedLayout);
        } else {
          // Unknown version, use default
          setLayoutState(DEFAULT_LAYOUT);
        }
      } else {
        setLayoutState(DEFAULT_LAYOUT);
      }
    } catch (error) {
      console.error('Error loading home layout from localStorage:', error);
      setLayoutState(DEFAULT_LAYOUT);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Save layout to localStorage
   */
  const saveLayout = useCallback((newLayout) => {
    if (!userId) return;

    const layoutToSave = {
      ...newLayout,
      lastModified: new Date().toISOString(),
    };

    try {
      const key = getStorageKey(userId);
      localStorage.setItem(key, JSON.stringify(layoutToSave));
      setLayoutState(layoutToSave);
    } catch (error) {
      console.error('Error saving home layout to localStorage:', error);
    }
  }, [userId]);

  /**
   * Update layout by modifying rows
   * @param {Array} newRows - New row configuration
   */
  const updateRows = useCallback((newRows) => {
    const newLayout = {
      ...layout,
      rows: newRows,
    };
    saveLayout(newLayout);
  }, [layout, saveLayout]);

  /**
   * Hide a widget (move to hiddenWidgets)
   * @param {string} widgetId - Widget ID to hide
   * @returns {boolean} - Success (false if last widget)
   */
  const hideWidget = useCallback((widgetId) => {
    // Count visible widgets
    const visibleCount = layout.rows.reduce(
      (count, row) => count + row.widgets.length,
      0
    );

    // Prevent hiding last widget (FR-016)
    if (visibleCount <= 1) {
      return false;
    }

    const newRows = layout.rows.map((row) => ({
      ...row,
      widgets: row.widgets.filter((id) => id !== widgetId),
    }));

    const newLayout = {
      ...layout,
      rows: newRows,
      hiddenWidgets: [...layout.hiddenWidgets, widgetId],
    };

    saveLayout(newLayout);
    return true;
  }, [layout, saveLayout]);

  /**
   * Show a widget (restore from hiddenWidgets)
   * @param {string} widgetId - Widget ID to show
   * @param {string} targetRowId - Row to add widget to (optional, defaults to first row)
   */
  const showWidget = useCallback((widgetId, targetRowId = null) => {
    // Find target row or use first row
    const rowIndex = targetRowId
      ? layout.rows.findIndex((r) => r.id === targetRowId)
      : 0;

    const targetIndex = rowIndex >= 0 ? rowIndex : 0;

    const newRows = layout.rows.map((row, idx) => {
      if (idx === targetIndex) {
        return {
          ...row,
          widgets: [...row.widgets, widgetId],
        };
      }
      return row;
    });

    const newLayout = {
      ...layout,
      rows: newRows,
      hiddenWidgets: layout.hiddenWidgets.filter((id) => id !== widgetId),
    };

    saveLayout(newLayout);
  }, [layout, saveLayout]);

  /**
   * Move widget to a new position
   * @param {string} widgetId - Widget to move
   * @param {string} toRowId - Target row ID
   * @param {number} toIndex - Target index in row
   */
  const moveWidget = useCallback((widgetId, toRowId, toIndex) => {
    // Get all widgets as a flat list (in order across all rows)
    const allWidgets = layout.rows.flatMap(row => row.widgets);
    
    // Remove the widget from the list
    const filteredWidgets = allWidgets.filter(id => id !== widgetId);
    
    // Clamp toIndex to valid range
    const clampedIndex = Math.max(0, Math.min(toIndex, filteredWidgets.length));
    
    // Insert at the new position
    filteredWidgets.splice(clampedIndex, 0, widgetId);
    
    // Put all widgets in the first row (simplified layout)
    const newRows = [
      { id: 'row-1', widgets: filteredWidgets },
    ];
    
    saveLayout({ ...layout, rows: newRows });
  }, [layout, saveLayout]);

  /**
   * Move widget to a specific row position (reorder rows)
   * @param {string} widgetId - Widget to move
   * @param {number} toRowIndex - Target row index (0-based)
   */
  const moveWidgetToRowIndex = useCallback((widgetId, toRowIndex) => {
    // Find current row containing the widget
    const currentRowIndex = layout.rows.findIndex((row) => 
      row.widgets.includes(widgetId)
    );

    if (currentRowIndex === -1 || currentRowIndex === toRowIndex) {
      return; // Widget not found or already at target position
    }

    // Create a copy of rows
    const newRows = [...layout.rows];
    
    // Get the row containing the widget
    const currentRow = newRows[currentRowIndex];
    
    // Remove the row from its current position
    newRows.splice(currentRowIndex, 1);
    
    // Insert the row at the new position
    // Adjust index if we removed from before the target position
    const adjustedIndex = currentRowIndex < toRowIndex ? toRowIndex - 1 : toRowIndex;
    newRows.splice(adjustedIndex, 0, currentRow);

    saveLayout({ ...layout, rows: newRows });
  }, [layout, saveLayout]);

  /**
   * Reset to default layout
   */
  const resetToDefault = useCallback(() => {
    saveLayout(DEFAULT_LAYOUT);
  }, [saveLayout]);

  /**
   * Get all visible widgets as flat array
   * @returns {Array<string>} Widget IDs
   */
  const getVisibleWidgets = useCallback(() => {
    return layout.rows.flatMap((row) => row.widgets);
  }, [layout]);

  /**
   * Check if a widget is visible
   * @param {string} widgetId - Widget ID
   * @returns {boolean}
   */
  const isWidgetVisible = useCallback((widgetId) => {
    return getVisibleWidgets().includes(widgetId);
  }, [getVisibleWidgets]);

  return {
    layout,
    isLoading,
    updateRows,
    hideWidget,
    showWidget,
    moveWidget,
    moveWidgetToRowIndex,
    resetToDefault,
    getVisibleWidgets,
    isWidgetVisible,
    hiddenWidgets: layout.hiddenWidgets,
  };
};

export default useHomeLayout;
